import React, { useRef, useEffect, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import get from 'lodash/get'

const createRootElement = (id: string) => {
  const rootContainer = document.createElementNS(
    'http://www.w3.org/1999/xhtml',
    'span'
  )
  rootContainer.setAttribute('id', id)
  rootContainer.setAttribute('data-portal-root', 'true')
  return rootContainer
}

const addRootElement = (rootElem: HTMLElement) => {
  document.body.insertBefore(
    rootElem,
    get(document, 'body.lastElementChild.nextElementSibling', null)
  )
}

export const usePortal = (id: string) => {
  const rootElemRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const existingParents = Array.from(
      document.querySelectorAll<HTMLElement>(`#${id}`)
    )
    const explicitParent = existingParents.find(
      (elem) => elem.getAttribute('data-portal-root') !== 'true'
    )
    const fallbackParent = existingParents.find(
      (elem) => elem.getAttribute('data-portal-root') === 'true'
    )
    const parentElem = explicitParent || fallbackParent || createRootElement(id)

    if (!explicitParent && !fallbackParent) {
      addRootElement(parentElem)
    }

    if (rootElemRef.current) {
      parentElem.appendChild<HTMLElement>(rootElemRef.current)
    }

    return () => {
      if (!!rootElemRef.current) {
        rootElemRef.current.remove()
      }
      if (
        parentElem.childNodes.length === 0 &&
        parentElem.getAttribute('data-portal-root') === 'true'
      ) {
        parentElem.remove()
      }
    }
  }, [])

  const getRootElem = () => {
    if (!rootElemRef.current) {
      rootElemRef.current = document.createElementNS(
        'http://www.w3.org/1999/xhtml',
        'span'
      )
    }
    return rootElemRef.current
  }
  return getRootElem()
}

const Portal = ({ id, children }: { id: string; children: ReactNode }) => {
  const target = usePortal(id)
  return createPortal(children, target)
}

export default Portal
